import type { Editor } from '@tiptap/react';

import { updateTaskItemAttrs } from './taskListItemAttrUtils';
import type { TaskItemAssignment } from './taskListItemChipTypes';

/** Reads the `assignments` attr off a task item node's attrs, defaulting to `[]`. */
export const getTaskItemAssignments = (attrs: Record<string, unknown> | undefined): TaskItemAssignment[] =>
    (attrs?.assignments as TaskItemAssignment[] | undefined) ?? [];

/** Reads the `showAssignments` attr off a task item node's attrs, defaulting to `false`. */
export const getTaskItemShowAssignments = (attrs: Record<string, unknown> | undefined): boolean =>
    Boolean(attrs?.showAssignments);

/**
 * Replaces the assignee list with the picked assignment — a task functionally
 * has a single assignee (mirrors the legacy `assignTask`, which writes a
 * one-element array), even though the schema stores a list. Returns the same
 * ref when the picked user is already the sole assignee. Shared by the chip
 * popup's `updateAttributes` path and the toolbar/"@" write paths so the
 * single-assignee rule lives in one place.
 */
export const replaceAssignmentList = (
    assignments: TaskItemAssignment[],
    assignment: TaskItemAssignment,
): TaskItemAssignment[] =>
    assignments.length === 1 && assignments[0].assigneeId === assignment.assigneeId ? assignments : [assignment];

/**
 * Sets the assignment on the task item node at `position`, replacing any
 * existing assignees. Also sets `showAssignments` so the chip stays visible.
 */
export const setTaskItemAssignment = ({
    editor,
    position,
    assignment,
}: {
    editor: Editor;
    position: number;
    assignment: TaskItemAssignment;
}): void => {
    updateTaskItemAttrs({
        editor,
        position,
        update: (attrs) => ({
            assignments: replaceAssignmentList(getTaskItemAssignments(attrs), assignment),
            showAssignments: true,
        }),
    });
};

/**
 * Toggles the "Assign to" placeholder chip on the task item node at `position`,
 * clearing any assignees either way (mirrors the element
 * `toggleElementShowAssignments`). Returns the new `showAssignments` value, or
 * `undefined` when there is no node at `position`.
 */
export const toggleTaskItemShowAssignments = ({
    editor,
    position,
}: {
    editor: Editor;
    position: number;
}): boolean | undefined => {
    const attrs = updateTaskItemAttrs({
        editor,
        position,
        update: (currentAttrs) => ({
            assignments: [],
            showAssignments: !getTaskItemShowAssignments(currentAttrs),
        }),
    });

    return attrs && getTaskItemShowAssignments(attrs);
};
